import { Button, Input, Row, Spin, Table, Tooltip } from 'antd'
import { FilterOutlined } from '@ant-design/icons'
import { FC } from 'react'
import Select from 'antd/es/select'
import { useTranslation } from 'react-i18next'
import { ColumnsType } from 'antd/es/table'

import { ContentContainer, StyledRow, StyledSelect } from '../StyledComponents'
import { useCarriers, useUserResponseManager } from '../../hooks'
import { useGeneral, useUser } from '../../contexts'
import { Driver, DriverByCarrierData, Trailer, Truck } from '../../types'
import useDriversView from '../../hooks/useDriversView'
import { useFiltersV2 } from '../../contexts/FiltersContextV2'

const { Option } = Select

const DriversView: FC = () => {
  const { t } = useTranslation()
  const { state: user } = useUser()
  const { filters, handleFilterChange } = useFiltersV2()
  const { filtersDisplayed, handleDisplayFilters, menuDisplayed } = useGeneral()
  const { drivers, isLoading } = useDriversView({ filters })
  const { userIsLoading } = useUserResponseManager(user?.id)
  const { allCarriersOptions } = useCarriers()

  const rowClassName = (_record: unknown, index: number) =>
    index % 2 === 0 ? 'zebra-even' : 'zebra-odd'

  const columnsDrivers: ColumnsType = [
    {
      title: t('tpl_dashboard.carrier'),
      dataIndex: 'driverByCarrierData',
      key: 'carrier',
      render: (driverByCarrierData: DriverByCarrierData[]) => (
        <div>
          {driverByCarrierData?.map((data, index) => (
            <div key={index}>{data.carrier?.businessName}</div>
          ))}
        </div>
      ),
    },
    {
      title: t('tpl_dashboard.name'),
      dataIndex: 'firstName',
      key: 'firstName',
    },
    {
      title: t('tpl_dashboard.last_name'),
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: t('tpl_dashboard.ID'),
      dataIndex: 'stateIdNumber',
      key: 'stateIdNumber',
    },
    {
      title: t('tpl_dashboard.phone'),
      dataIndex: 'phone',
      width: '14.5%',
      key: 'phone',
      render: (phone: string, record: Driver) => (
        <Row style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>{phone}</div>
          <Tooltip
            title={
              // eslint-disable-next-line no-nested-ternary
              record.errorSendingValidationMessage
                ? t('tpl_dashboard.error')
                : record.phoneVerifiedAt
                ? t('tpl_dashboard.validated')
                : t('tpl_dashboard.pending')
            }
            placement="right"
          >
            <span
              style={{
                // eslint-disable-next-line no-nested-ternary
                color: record.errorSendingValidationMessage
                  ? 'red'
                  : record.phoneVerifiedAt
                  ? 'green'
                  : 'yellow',
                fontSize: '4em',
                marginLeft: '5px',
                lineHeight: '0em',
              }}
            >
              &#x2022;
            </span>
          </Tooltip>
        </Row>
      ),
    },
    {
      title: t('tpl_dashboard.preferred_truck'),
      dataIndex: 'preferredTrucks',
      key: 'preferredTruck',
      render: (preferredTrucks: Truck[]) => (
        <div>
          {preferredTrucks?.length > 0 ? (
            preferredTrucks.map((truck, index) => (
              <div key={index}>
                {truck.equipmentType
                  ? `${truck.licensePlate} - ${truck.equipmentType.name} - ${truck.description}`
                  : `${truck.licensePlate} - ${truck.description}`}
              </div>
            ))
          ) : (
            <span>{t('globals.generics.has_not')}</span>
          )}
        </div>
      ),
    },
    {
      title: t('tpl_dashboard.preferred_trailer'),
      dataIndex: 'preferredTrailers',
      key: 'preferredTrailer',
      render: (preferredTrailers: Trailer[]) => (
        <div>
          {preferredTrailers?.length > 0 ? (
            preferredTrailers.map((trailer, index) => (
              <div key={index}>
                {trailer.equipmentType
                  ? `${trailer.licensePlate} - ${trailer.equipmentType.name} - ${trailer.description}`
                  : `${trailer.licensePlate} - ${trailer.description}`}
              </div>
            ))
          ) : (
            <span>{t('globals.generics.has_not')}</span>
          )}
        </div>
      ),
    },

    {
      title: t('tpl_dashboard.status'),
      dataIndex: 'driverByCarrierData',
      key: 'status',
      render: (driverByCarrierData: DriverByCarrierData[]) => {
        const allDisabled = driverByCarrierData.every(data => data.disableAt !== null)

        return allDisabled ? (
          <span style={{ color: 'red' }}>DESHABILITADO</span>
        ) : (
          <span>HABILITADO</span>
        )
      },
    },
  ]

  return (
    <ContentContainer>
      <StyledRow className="sticky-options">
        <div
          className={`filters-container${
            filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
          }`}
        >
          <Button onClick={handleDisplayFilters} id="displayFiltersButton">
            <FilterOutlined />
          </Button>
          <div className="filters-list">
            <StyledSelect
              maxTagCount="responsive"
              allowClear
              value={
                filters.carrierId
                  ? (filters.carrierId as number[]).map(value => Number(value))
                  : undefined
              }
              mode="multiple"
              optionFilterProp="label"
              placeholder={t('tpl_dashboard.carrier')}
              onChange={handleFilterChange('carrierId')}
              options={allCarriersOptions?.map(option => ({
                label: option.label,
                value: option.value,
              }))}
            />
            <Input
              style={{ flex: 1 }}
              placeholder={t('tpl_router.tpl_dashboard.edit_truck_modal.search_name')}
              type="string"
              onPressEnter={e => {
                if (e.currentTarget.value === '' || !e.currentTarget.value) {
                  handleFilterChange('nameSurname')(undefined)
                } else {
                  handleFilterChange('nameSurname')(e.currentTarget.value)
                }
              }}
              onBlur={e => {
                if (e.currentTarget.value === '' || !e.currentTarget.value) {
                  handleFilterChange('nameSurname')(undefined)
                } else {
                  handleFilterChange('nameSurname')(e.currentTarget.value)
                }
              }}
              onChange={e => {
                if (e.currentTarget.value === '' || !e.currentTarget.value) {
                  handleFilterChange('nameSurname')(undefined)
                }
              }}
              allowClear
            />
            <StyledSelect
              maxTagCount="responsive"
              allowClear
              value={filters.disabled}
              placeholder={t('tpl_dashboard.state')}
              onChange={handleFilterChange('disabled')}
            >
              <Option value={false}>{t('globals.generics.enabled')}</Option>
              <Option value={true}>{t('globals.generics.disabled')}</Option>
            </StyledSelect>
          </div>
        </div>
      </StyledRow>
      <StyledRow className="ticket-handler">
        <div style={{ flex: 1 }} />
        {userIsLoading ? <Spin /> : <div style={{ flex: 1 }} />}
      </StyledRow>
      <Table
        columns={columnsDrivers}
        dataSource={drivers?.drivers}
        loading={isLoading}
        rowKey="id"
        rowClassName={rowClassName}
        pagination={{
          pageSize: filters.limit as number,
          onChange: handleFilterChange('page', false),
          total: drivers?.total,
          showSizeChanger: false,
          current: filters.page as number,
        }}
      />
    </ContentContainer>
  )
}

export default DriversView
